import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Video, Calendar, Users, CheckCircle2, AlertCircle, Loader2, FileText, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  getMeetings,
  getMeetingSummary,
  getMeetingRecordingUrl,
  generateContractFromMeeting,
  sendMeetingSummaryEmail,
  Meeting,
  MeetingSummary,
} from '../../services/Calendar';
import ContractDocumentCard from './ContractDocumentCard';
import { useFeatureToggle } from '../../hooks/useFeatureToggle';
import { useI18n } from '../../i18n';

const formatMeetingDateTime = (startTime: string, endTime: string, locale: string): string => {
  const start = new Date(startTime);
  const end = new Date(endTime);

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: locale === 'en',
  };

  const intlLocale = locale === 'sv' ? 'sv-SE' : 'en-US';
  const dateStr = start.toLocaleDateString(intlLocale, dateOptions);
  const startTimeStr = start.toLocaleTimeString(intlLocale, timeOptions);
  const endTimeStr = end.toLocaleTimeString(intlLocale, timeOptions);

  return `${dateStr} • ${startTimeStr} - ${endTimeStr}`;
};

const getPlatformDisplay = (platform: string | null): string => {
  if (!platform) return 'Meeting';

  switch (platform.toLowerCase()) {
    case 'zoom':
      return 'Zoom';
    case 'google_meet':
      return 'Google Meet';
    case 'microsoft_teams':
      return 'Teams';
    default:
      return platform;
  }
};

const MeetingDetailPage: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const m = t.meetings.detail;
  const { isEnabled: canUseManualTriggers } = useFeatureToggle('meeting-manual-triggers');

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [summary, setSummary] = useState<MeetingSummary | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(true);
  const [error, setError] = useState<'notFound' | 'loadFailed' | null>(null);
  const [contractGenerating, setContractGenerating] = useState(false);
  const [contractResult, setContractResult] = useState<{ success: boolean; contractId?: string; error?: string } | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [includeContract, setIncludeContract] = useState(false);

  useEffect(() => {
    if (!meetingId) return;

    // Reset state when meetingId changes
    setVideoUrl(null);
    setSummary(null);

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch meeting details
        const meetingsResponse = await getMeetings();
        if (meetingsResponse.success && meetingsResponse.data) {
          const foundMeeting = meetingsResponse.data.find(m => m.id === meetingId);
          if (foundMeeting) {
            setMeeting(foundMeeting);
          } else {
            setError('notFound');
          }
        }
      } catch (err) {
        setError('loadFailed');
      } finally {
        setLoading(false);
      }
    };

    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const summaryResponse = await getMeetingSummary(meetingId);
        if (summaryResponse.success && summaryResponse.data) {
          setSummary(summaryResponse.data);
        }
      } catch (err) {
        // Summary might not exist yet - that's okay
      } finally {
        setSummaryLoading(false);
      }
    };

    const fetchVideo = async () => {
      setVideoLoading(true);
      try {
        const videoResponse = await getMeetingRecordingUrl(meetingId);
        if (videoResponse.success && videoResponse.data?.available) {
          setVideoUrl(videoResponse.data.videoUrl);
        } else {
          setVideoUrl(null);
        }
      } catch (err) {
        // Video might not exist - that's okay
        setVideoUrl(null);
      } finally {
        setVideoLoading(false);
      }
    };

    fetchData();
    fetchSummary();
    fetchVideo();
  }, [meetingId]);

  const handleGenerateContract = async () => {
    if (!meetingId) return;
    
    setContractGenerating(true);
    setContractResult(null);
    
    try {
      const response = await generateContractFromMeeting(meetingId);
      if (response.success && response.data) {
        setContractResult({ success: true, contractId: response.data.contractId });
      } else {
        const errorMessage = response.errors?.[0]?.title || m.generateContractFailed;
        setContractResult({ success: false, error: errorMessage });
      }
    } catch (err) {
      setContractResult({ success: false, error: m.generateContractFailed });
    } finally {
      setContractGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!meetingId) return;
    
    setEmailSending(true);
    setEmailResult(null);
    
    try {
      const response = await sendMeetingSummaryEmail(meetingId, includeContract);
      if (response.success && response.data?.success) {
        setEmailResult({ success: true });
      } else {
        const errorMessage = response.errors?.[0]?.title || m.sendEmailFailed;
        setEmailResult({ success: false, error: errorMessage });
      }
    } catch (err) {
      setEmailResult({ success: false, error: m.sendEmailFailed });
    } finally {
      setEmailSending(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-6 w-96" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{m.error}</AlertTitle>
          <AlertDescription>{m[error ?? 'notFound']}</AlertDescription>
        </Alert>
        <Button variant="ghost" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {m.goBack}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back Button */}
      <Button variant="ghost" className="mb-4" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {m.backToMeetings}
      </Button>

      {/* Meeting Header */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-2xl font-bold">{meeting.title}</h1>
          {meeting.platform && (
            <Badge variant="outline" className="flex-shrink-0">
              <Video className="h-3 w-3 mr-1" />
              {getPlatformDisplay(meeting.platform)}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>{formatMeetingDateTime(meeting.startTime, meeting.endTime, locale)}</span>
        </div>
      </div>

      <Separator />

      {/* Video Player */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            {m.recording}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {videoLoading ? (
            <Skeleton className="aspect-video w-full rounded-lg" />
          ) : videoUrl ? (
            <video
              controls
              className="w-full rounded-lg bg-black aspect-video"
              src={videoUrl}
            >
              <source src={videoUrl} type="video/mp4" />
              {m.videoUnsupported}
            </video>
          ) : (
            <div className="aspect-video w-full rounded-lg bg-muted flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Video className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>{m.recordingUnavailable}</p>
                <p className="text-sm">{m.recordingUnavailableHint}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Section */}
      {summaryLoading ? (
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      ) : summary?.status === 'processing' ? (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin" />
          <AlertTitle>{m.processing}</AlertTitle>
          <AlertDescription>
            {m.processingDescription}
          </AlertDescription>
        </Alert>
      ) : summary?.status === 'failed' ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{m.summaryFailed}</AlertTitle>
          <AlertDescription>
            {summary.errorMessage || m.summaryFailedDescription}
          </AlertDescription>
        </Alert>
      ) : summary?.analysis ? (
        <>
          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle>{m.summary}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {summary.analysis.summary}
              </p>
            </CardContent>
          </Card>

          {/* Key Topics */}
          {summary.analysis.keyTopics.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{m.keyTopics}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {summary.analysis.keyTopics.map((topic, index) => (
                    <div key={index} className="space-y-1">
                      <h4 className="font-medium">{topic.title}</h4>
                      <p className="text-sm text-muted-foreground">{topic.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Items */}
          {(summary.analysis.generalActions.length > 0 || summary.analysis.assignedActions.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  {m.actionItems}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* General Actions */}
                {summary.analysis.generalActions.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">{m.general}</h4>
                    <ul className="space-y-2">
                      {summary.analysis.generalActions.map((action, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                          <span className="text-sm">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Assigned Actions */}
                {summary.analysis.assignedActions.map((group, groupIndex) => (
                  <div key={groupIndex} className="space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      {group.assignee}
                    </h4>
                    <ul className="space-y-2 pl-6">
                      {group.actions.map((action, actionIndex) => (
                        <li key={actionIndex} className="flex items-start gap-2">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                          <span className="text-sm">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Contract Detection Section - QUERI only */}
          {canUseManualTriggers && summary.contractDetected && summary.contractMetadata && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {m.contractDetected}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{summary.contractMetadata.documentType}</Badge>
                    <Badge variant="outline">
                      {m.confidence(Math.round(summary.contractMetadata.confidence * 100))}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {summary.contractMetadata.documentDescription}
                  </p>
                </div>

                {summary.contractMetadata.parties.length > 0 && (
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium">{m.parties}</h4>
                    <ul className="text-sm text-muted-foreground">
                      {summary.contractMetadata.parties.map((party, idx) => (
                        <li key={idx}>
                          {party.role}: {party.name || m.notSpecified}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {summary.contractMetadata.keyTerms.length > 0 && (
                  <div className="space-y-1">
                    <h4 className="text-sm font-medium">{m.keyTerms}</h4>
                    <ul className="text-sm text-muted-foreground">
                      {summary.contractMetadata.keyTerms.map((term, idx) => (
                        <li key={idx}>
                          {term.term}: {term.value || m.notSpecified}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Separator />

                {contractResult?.success ? (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>{m.contractGenerated}</AlertTitle>
                    <AlertDescription>
                      {m.contractId(contractResult.contractId ?? '')}
                      <br />
                      <span className="text-sm text-muted-foreground">
                        {m.findInDrafts}
                      </span>
                    </AlertDescription>
                  </Alert>
                ) : contractResult?.error ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{m.generationFailed}</AlertTitle>
                    <AlertDescription>{contractResult.error}</AlertDescription>
                  </Alert>
                ) : (
                  <Button 
                    onClick={handleGenerateContract} 
                    disabled={contractGenerating}
                    className="w-full"
                  >
                    {contractGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {m.generatingContract}
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        {m.generateContract}
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contract Document Section - show when document exists */}
          {summary.hasContractDocx && meetingId && (
            <ContractDocumentCard
              meetingId={meetingId}
              documentType={summary.contractMetadata?.documentType}
            />
          )}

          {/* Email Actions Section - QUERI only */}
          {canUseManualTriggers && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  {m.emailActions}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{m.emailRecipient}</p>
                  <p className="font-medium">{summary.recipientEmail}</p>
                </div>

                {summary.hasContractDocx && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeContract"
                      checked={includeContract}
                      onCheckedChange={(checked) => setIncludeContract(checked === true)}
                    />
                    <Label htmlFor="includeContract" className="text-sm cursor-pointer">
                      {m.includeContract}
                    </Label>
                  </div>
                )}

                <Separator />

                {emailResult?.success ? (
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>{m.emailSent}</AlertTitle>
                    <AlertDescription>
                      {m.emailSentDescription(summary.recipientEmail)}
                    </AlertDescription>
                  </Alert>
                ) : emailResult?.error ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>{m.emailFailed}</AlertTitle>
                    <AlertDescription>{emailResult.error}</AlertDescription>
                  </Alert>
                ) : (
                  <Button
                    onClick={handleSendEmail}
                    disabled={emailSending}
                    className="w-full"
                  >
                    {emailSending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {m.sendingEmail}
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        {m.sendEmail}
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

        </>
      ) : (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{m.noSummary}</AlertTitle>
          <AlertDescription>
            {m.noSummaryDescription}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default MeetingDetailPage;


